import { create } from "zustand";
import { friendStore } from "./friend-store";
import { chatStore } from "./chat-store";
import { useGlobalStore } from "./global-store";
interface WebSocketState {
    ws: WebSocket | null;
    isConnected: boolean;
    lastMessage: MessageEvent | null;
    sendMessage: (message: string | ArrayBufferLike | Blob) => void;
    connect: (url: string, options?: WebSocketOptions) => void;
    disconnect: () => void;
}

interface WebSocketOptions {
    onOpen?: (event: Event) => void;
    onMessage?: (event: MessageEvent) => void;
    onClose?: (event: CloseEvent) => void;
    onError?: (event: Event) => void;
    reconnectInterval?: number; // WebSocket 连接断开后，尝试重新连接的间隔时间（通常以毫秒为单位）
    reconnectLimit?: number; // 尝试重新连接的最大次数
    heartbeatInterval?: number; // 心跳间隔时间，默认30秒
}

const useWebSocketStore = create<WebSocketState>((set, get) => {
    let reconnectAttempts = 0;
    let reconnectTimer: NodeJS.Timeout | null = null;
    let heartbeatTimer: NodeJS.Timeout | null = null;
    let heartbeatTimeout: NodeJS.Timeout | null = null; // 新增：心跳超时定时器
    // 手动断开时不进行重连
    let shouldReconnect = true;
    // 发送心跳
    const sendHeartbeat = (): void => {
        const { ws } = get();
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ event: "ping" }));
            // 新增：设置超时定时器，10秒未收到pong则关闭连接
            heartbeatTimeout = setTimeout(() => {
                console.log("[WS DEBUG] 心跳超时，关闭连接,重置重连尝试次数");
                reconnectAttempts = 0;
                ws.close(4000, "Heartbeat timeout");
            }, 10000);
        }
    };

    // 启动心跳
    const startHeartbeat = (interval = 30000): void => {
        if (heartbeatTimer) {
            clearInterval(heartbeatTimer);
        }
        heartbeatTimer = setInterval(sendHeartbeat, interval);
    };

    // 停止心跳
    const stopHeartbeat = (): void => {
        if (heartbeatTimer) {
            clearInterval(heartbeatTimer);
            heartbeatTimer = null;
        }
    };

    const connect = (url: string, options?: WebSocketOptions): void => {
        // 主动连接时允许重连
        shouldReconnect = true;
        const { ws: existingWs } = get();
        if (existingWs && existingWs.readyState === WebSocket.OPEN) {
            console.log("[WS DEBUG]已连接", existingWs, get().isConnected);
            return;
        }

        const ws = new WebSocket(url);
        ws.onopen = event => {
            set({ ws, isConnected: true, lastMessage: null });
            // 成功连接后重置重连尝试次数
            reconnectAttempts = 0;
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            // 连接成功后启动心跳
            startHeartbeat(options?.heartbeatInterval);
            // 如果当前全局 userStatus 为 offline，置为 online
            try {
                const gs = useGlobalStore.getState();
                if (gs.userStatus === "offline") {
                    gs.updateOnlineStatus("online");
                }
            } catch {
                // ignore
            }
            options?.onOpen?.(event);
        };

        ws.onmessage = event => {
            set({ lastMessage: event });
            try {
                const data = JSON.parse(event.data);
                handleMessageEvent(data);
            } catch (error) {
                console.log("[WS DEBUG] 消息解析失败", event.data, error);
            }
            options?.onMessage?.(event);
        };

        ws.onclose = event => {
            set({ isConnected: false });
            // 连接关闭时停止心跳
            stopHeartbeat();
            // 新增：清理心跳超时定时器
            if (heartbeatTimeout) {
                clearTimeout(heartbeatTimeout);
                heartbeatTimeout = null;
            }
            options?.onClose?.(event);
            // 手动断开：不进行重连
            if (!shouldReconnect) {
                reconnectAttempts = 0;
                return;
            }
            if (
                options?.reconnectLimit === undefined ||
                reconnectAttempts < options.reconnectLimit
            ) {
                reconnectAttempts++;
                reconnectTimer = setTimeout(
                    () => connect(url, options),
                    options?.reconnectInterval || 1000,
                );
            } else {
                // 重连次数耗尽，将全局 userStatus 置为 offline
                const gs = useGlobalStore.getState();
                gs.updateOnlineStatus("offline");
            }
        };

        ws.onerror = event => {
            console.error("WebSocket Error:", event);
            options?.onError?.(event);
            // 发生错误时停止心跳
            stopHeartbeat();
            // 新增：清理心跳超时定时器
            if (heartbeatTimeout) {
                clearTimeout(heartbeatTimeout);
                heartbeatTimeout = null;
            }
            ws.close(); // Close on error to trigger onclose and potential reconnect
        };
    };

    const disconnect = (): void => {
        const { ws } = get();
        if (ws) {
            // 标记手动断开，阻止 onclose 中的重连
            shouldReconnect = false;
            ws.close();
            set({ ws: null, isConnected: false, lastMessage: null });
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            // 额外清理心跳和超时定时器，防止残留任务
            stopHeartbeat();
            if (heartbeatTimeout) {
                clearTimeout(heartbeatTimeout);
                heartbeatTimeout = null;
            }
        }
    };

    const sendMessage = (message: string | ArrayBufferLike | Blob): void => {
        const { ws } = get();
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(message);
        } else {
            console.warn("[WS DEBUG] 消息发送失败", message);
        }
    };

    const handleMessageEvent = ({ event, payload }: { event: string; payload?: any }): void => {
        const { updateApplicationList, updateOnlineList, updateIntimate } = friendStore;
        const { updateReactionList } = chatStore;
        switch (event) {
            case "pong": {
                if (heartbeatTimeout) {
                    clearTimeout(heartbeatTimeout);
                    heartbeatTimeout = null;
                }
                break;
            }
            case "getRequests": {
                // 好友申请列表
                updateApplicationList(payload);
                break;
            }
            case "add": {
                // 收到新的好友申请
                updateApplicationList(payload);
                break;
            }
            case "online": {
                // 会话列表在线状态更新
                updateOnlineList(payload);
                break;
            }
            case "emoji": {
                // 消息表情列表更新
                updateReactionList(payload);
                break;
            }
            case "intimate": {
                // 亲密关系邀请更新
                console.log("[WS DEBUG]亲密关系邀请更新", payload);
                updateIntimate(payload);
                break;
            }
            case "error": {
                console.log("【WS LOG】ERROR:", payload);
                break;
            }

            default: {
                break;
            }
        }
    };

    return {
        ws: null,
        isConnected: false,
        lastMessage: null,
        sendMessage,
        connect,
        disconnect,
    };
});

export default useWebSocketStore;
